import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage
} from '@react-pdf/renderer';
import { useEffect, useState } from 'react';
import React from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  text: {
    fontSize: 8,
    marginLeft: 10,
    marginRight: 10
  },
  textUnderlined: {
    fontSize: 8,
    textDecoration: 'underline'
  },
  heading: {
    fontSize: 14,
    fontWeight: 'ultrabold',
    marginBottom: 10,
    textAlign: 'center'
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 40,
    marginTop: 30,
    border: '1.2px solid black',
    paddingLeft: 20,
    paddingRight: 70,
    // paddingBottom: 40,
    paddingTop: 5
  },
  column1: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    marginRight: '10%'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    marginLeft: '5%'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: 5
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    padding: 5,
    border: '1px solid black',
    marginTop: 10,
    backgroundColor: 'navy',
    color: 'white'
  },
  headerText: {
    fontSize: 8,
    fontWeight: 'bold',
    marginLeft: 25,
    marginRight: 25
  },
  columnHeader: {
    width: 'auto'
  },
  columnText: {
    width: '10%',
    textAlign: 'center'
  },
  columnTextWide: {
    width: '17%',
    textAlign: 'center'
  },
  columnTextNarrow: {
    width: '5%',
    textAlign: 'center'
  }
});

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getCurrentDate = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const renderServiceRows = (indoorServices, admissionDate) => {
  return indoorServices.flatMap((serviceEntry, entryIndex) => {
    return serviceEntry.services.map((item, itemIndex) => (
      <View
        style={{
          ...styles.row,
          backgroundColor: (entryIndex + itemIndex) % 2 !== 0 ? '#ffffff' : '#f7f7f7',
          marginTop: 5
        }}
        key={`${entryIndex}-${itemIndex}`}
      >
        <Text content={`${entryIndex + 1}`} style={{ ...styles.columnTextNarrow, width: '10%' }} />
        <Text content={admissionDate} style={{ ...styles.columnText, width: '10%' }} />
        <Text
          content={item.service_name ? item.service_name : 'N/A'}
          style={{ ...styles.columnTextWide, width: '25%' }}
        />
        <Text content={item.quantity ? item.quantity : 'N/A'} style={{ ...styles.columnText, width: '5%' }} />
        <Text content={item.rate ? item.rate : 'N/A'} style={{ ...styles.columnText, width: '10%' }} />
        <Text content={item.disc_amt ? item.disc_amt : 'N/A'} style={{ ...styles.columnText, width: '10%' }} />
        <Text content={item.net_amt ? item.net_amt : 'N/A'} style={{ ...styles.columnText, width: '10%' }} />
      </View>
    ));
  });
};

const calculateSubtotal = (serviceItems = [], indoorService = []) => {
  const serviceItemsSubtotal = (serviceItems || []).reduce((total, item) => total + parseFloat(item.total_amt || 0), 0);
  const indoorServiceSubtotal = (indoorService || []).reduce(
    (total, item) => total + parseFloat(item.total_amt || 0),
    0
  );
  const totalSubtotal = serviceItemsSubtotal + indoorServiceSubtotal;
  return totalSubtotal;
};

const calculateTotalDiscount = (serviceItems = [], indoorService = []) => {
  const serviceItemsDiscount = (serviceItems || []).reduce((total, item) => total + parseFloat(item.disc_amt || 0), 0);
  const indoorServiceDiscount = (indoorService || []).reduce(
    (total, item) => total + parseFloat(item.disc_amt || 0),
    0
  );
  const totalDiscount = serviceItemsDiscount + indoorServiceDiscount;
  return totalDiscount;
};

const calculateNetAmount = (serviceItems = [], indoorService = []) => {
  // Sum the already-correct per-bill net_amt directly (amount - disc_amt + gst_amt,
  // see OPDBillingDetailForm.js) instead of re-deriving it from total_amt - disc_amt,
  // which silently drops gst_amt and understates the net amount whenever GST applies.
  const netAmount =
    (serviceItems || []).reduce((total, item) => total + parseFloat(item.net_amt || 0), 0) +
    (indoorService || []).reduce((total, item) => total + parseFloat(item.net_amt || 0), 0);
  return netAmount;
};

const calculateTotalReceipt = (serviceItems = [], indoorService = []) => {
  const serviceItemsTotalReceipt = (serviceItems || []).reduce(
    (total, item) => total + parseFloat(item.total_recpt || 0),
    0
  );
  const indoorServiceTotalReceipt = (indoorService || []).reduce(
    (total, item) => total + parseFloat(item.total_recpt || 0),
    0
  );
  const totalReceipt = serviceItemsTotalReceipt + indoorServiceTotalReceipt;
  console.log('HELLO TOTAL RECIEPT', totalReceipt);
  return totalReceipt;
};

const calculateTotalReceivedAmountVoucher = (receiptVouchers) => {
  if (!Array.isArray(receiptVouchers)) {
    return 0;
  }
  const totalReceivedAmount = receiptVouchers.reduce((total, voucher) => {
    return total + parseFloat(voucher.received_amount || 0);
  }, 0);
  return totalReceivedAmount;
};

const calculateTotalAmount = (paymentRefundVouchers) => {
  if (!Array.isArray(paymentRefundVouchers)) {
    return 0;
  }
  const totalrefund = paymentRefundVouchers.reduce((total, voucher) => {
    return total + parseFloat(voucher.amount || 0);
  }, 0);
  console.log('HELLO REFUND', totalrefund);
  return totalrefund;
};

export const FinalBillPdf = ({ data, isPublic = false }) => {

  const [barcodeImg, setBarcodeImg] = React.useState("");
  const [qrImg, setQrImg] = React.useState("");
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloaded, setIsDownloaded] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  // const [totalReceivedAmount, setTotalReceivedAmount] = useState(0);
  const [netAmountPaid, setNetAmountPaid] = useState(0);
  const subtotal = calculateSubtotal(data.service_items, data.indoorService);
  const totalDiscount = calculateTotalDiscount(data.service_items, data.indoorService);
  const netAmount = calculateNetAmount(data.service_items, data.indoorService);
  const totalReceipt = calculateTotalReceipt(data.service_items, data.indoorService);
  const totalReceivedAmountVoucher = calculateTotalReceivedAmountVoucher(data.receiptVouchers);
  const totalRefund = calculateTotalAmount(data.paymentRefundVouchers);
  const AllTotalRecieved = Number(totalReceipt) + Number(totalReceivedAmountVoucher);
  const currentDate = getCurrentDate();
  const admissionDate = formatDate(data.dateTime);
  const dischargeDate = formatDate(data.dischargeDate);
  const add_discount = data.add_discount;






  React.useEffect(() => {
    if (!data || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const refId = params.get("refId");

    // BARCODE
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(data.ipd_no), {
      format: "CODE128",
      displayValue: false,
      height: 40
    });
    setBarcodeImg(canvas.toDataURL());
    let path;
    // QR → PUBLIC LINK
    if (refId) {
      if (isPublic) {
        path = window.location.pathname;
      } else {
        path = window.location.pathname.replace("/print/", "/public/print/");
      }
      const qrUrl = `${window.location.origin}${path}?refId=${refId}`;
      QRCode.toDataURL(qrUrl).then(setQrImg);
    }
  }, [data]);


  const downloadPDF = async () => {
    try {
      // Import pdf dynamically to avoid SSR issues
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(PdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `OPD-Bill-${data.receiptNo || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setIsDownloading(false);

        // Show success message instead of trying to close
        setIsDownloaded(true);
      }, 100);

    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
      setIsError(true);
      //alert('Failed to download PDF. Please try again.');
    }
  };


  React.useEffect(() => {
    if (isPublic && data && barcodeImg && qrImg && !isDownloading) {
      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        downloadPDF();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isPublic, data, barcodeImg, qrImg, isDownloading]);


  const PdfDoc = (
    <Document title={`${data.patientName}'s Final Bill`}>
      <Page size="A4" style={styles.page}>
        <PDFImage
          src="/logo_Hospital_m.jpeg"
          style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
        />
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginVertical: 10
          }}
        >
          {barcodeImg && (
            <PDFImage src={barcodeImg} style={{ width: 130, height: 40 }} />
          )}

          {qrImg && (
            <PDFImage src={qrImg} style={{ width: 70, height: 70 }} />
          )}
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.column1}>
            <View style={styles.row}>
              <Text content="UHID NO.         :- " style={{ fontSize: 8 }} />
              <Text content={`${data.uhid}`} />
            </View>
            <View style={styles.row}>
              <Text content="Name               :- " style={{ fontSize: 8 }} />
              <Text content={`${data.patientName}`} />
            </View>
            <View style={styles.row}>
              <Text content="Age/Gender     :- " style={{ fontSize: 8 }} />
              <Text content={`${data.age} / ${data.gender}`} />
            </View>
            <View style={styles.row}>
              <Text content="Doctor              :- " style={{ fontSize: 8 }} />
              <Text content={`${data.consultant.doctor}`} />
            </View>
            <View style={styles.row}>
              <Text content="Department      :- " style={{ fontSize: 8 }} />
              <Text content={`${data.department}`} />
            </View>
            <View style={styles.row}>
              <Text content="Address            :- " style={{ fontSize: 8 }} />
              <Text content={`${data.address}`} />
            </View>
            <View style={styles.row}>
              <Text content="Ward/Bed No.   :- " style={{ fontSize: 8 }} />
              <Text content={`${'-'}  /  ${data?.consultantDetail?.bed_no}`} />
            </View>
          </View>
          <View style={styles.column}>
            <View style={styles.row}>
              <Text content="Ipd No                       :- " style={{ fontSize: 8 }} />
              <Text content={`${data.ipd_no}`} />
            </View>
            <View style={styles.row}>
              <Text content="Bill No.                       :- " style={{ fontSize: 8 }} />
              <Text content={`${data.receiptNo}`} />
            </View>
            <View style={styles.row}>
              <Text content="Bill Date                     :- " style={{ fontSize: 8 }} />
              <Text content={`${currentDate}`} />
            </View>
            <View style={styles.row}>
              <Text content="Addmission Date        :- " style={{ fontSize: 8 }} />
              <Text content={`${admissionDate}`} />
            </View>
            <View style={styles.row}>
              <Text content="Discharge Date           :- " style={{ fontSize: 8 }} />
              <Text content={`${dischargeDate && '-'}`} />
            </View>
            <View style={styles.row}>
              <Text content="Sponsor                      :- " style={{ fontSize: 8 }} />
              <Text content={`${data.sponsor}`} />
            </View>
            <View style={styles.row}>
              <Text content="TPA/Insurance            :- " style={{ fontSize: 8 }} />
              <Text content="" />
            </View>
          </View>
        </View>
        <View style={styles.headerContainer}>
          <Text content="S No." style={{ ...styles.columnTextNarrow, width: '10%' }} />
          <Text content="Date" style={{ ...styles.columnText, width: '10%' }} />
          <Text content="Description" style={{ ...styles.columnTextWide, width: '25%' }} />
          <Text content="Qty" style={{ ...styles.columnText, width: '5%' }} />
          <Text content="Rate" style={{ ...styles.columnText, width: '10%' }} />
          <Text content="Dis" style={{ ...styles.columnText, width: '10%' }} />
          <Text content="Amount (Rs)" style={{ ...styles.columnText, width: '10%' }} />
        </View>
        {renderServiceRows(data.indoorService, admissionDate)}
        <View style={{ ...styles.row }}>
          <Text
            content="Total Bill Amount:"
            style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '75%' }}
          />
          <Text content={`${subtotal}`} style={{ ...styles.columnText }} />
        </View>
        <View style={{ ...styles.row }}>
          <Text content="Discount:" style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '75%' }} />
          <Text content={`${totalDiscount}`} style={{ ...styles.columnText }} />
        </View>
        <View style={{ ...styles.row }}>
          <Text
            content="Net Bill Amount:"
            style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '75%' }}
          />
          <Text content={`${netAmount}`} style={{ ...styles.columnText }} />
        </View>
        <View style={{ ...styles.row }}>
          <Text
            content="Amount Received:"
            style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '75%' }}
          />
          <Text content={`${AllTotalRecieved}`} style={{ ...styles.columnText }} />
        </View>
        <View style={{ ...styles.row }}>
          <Text content="Balance:" style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '75%' }} />
          <Text
            content={netAmount - (AllTotalRecieved - totalRefund) - add_discount}
            style={{ ...styles.columnText }}
          />
        </View>
        <Text content="Reciept Voucher" style={styles.columnTextWide} />
        <View style={{ ...styles.headerContainer, width: '100%' }}>
          <Text content="S No." style={{ ...styles.columnTextNarrow, width: '10%' }} />
          <Text content="IPD No." style={{ ...styles.columnText, width: '10%' }} />
          <Text content="Payment Mode" style={{ ...styles.columnTextWide, width: '25%' }} />
          <Text content="Receipt No." style={{ ...styles.columnText, width: '15%' }} />
          <Text content="Payment Type" style={{ ...styles.columnText, width: '15%' }} />
          <Text content="Received Amount" style={{ ...styles.columnText, width: '30%' }} />
        </View>

        {data.receiptVouchers && data.receiptVouchers.length > 0 ? (
          data.receiptVouchers.map((voucher, index) => (
            <View
              style={{
                ...styles.row,
                backgroundColor: index % 2 !== 0 ? '#ffffff' : '#f7f7f7',
                marginTop: 5,
                width: '100%'
              }}
              key={voucher._id}
            >
              <Text content={index + 1} style={{ ...styles.columnTextNarrow, width: '10%' }} />
              <Text content={data.ipd_no || '-'} style={{ ...styles.columnText, width: '10%' }} />
              <Text content={voucher.payment_mode || '-'} style={{ ...styles.columnTextWide, width: '25%' }} />
              <Text content={voucher.receipt_no || '-'} style={{ ...styles.columnText, width: '15%' }} />
              <Text content={voucher.payment_type || '-'} style={{ ...styles.columnText, width: '15%' }} />
              <Text content={voucher.received_amount || '0'} style={{ ...styles.columnText, width: '30%' }} />
            </View>
          ))
        ) : (
          <View style={{ ...styles.row }}>
            <Text content="No receipts found" style={{ ...styles.columnTextNarrow, width: '100%' }} />
            <Text content="-" style={styles.columnText} />
            <Text content="-" style={styles.columnTextWide} />
            <Text content="-" style={styles.columnText} />
            <Text content="-" style={{ ...styles.columnText, width: '20%' }} />
          </View>
        )}
        <Text content="Refund Voucher" style={{ ...styles.columnTextWide, marginTop: '5%' }} />
        <View style={{ ...styles.headerContainer, width: '100%' }}>
          <Text content="Sr No." style={{ ...styles.columnTextNarrow, width: '10%' }} />
          <Text content="IPD No." style={{ ...styles.columnText, width: '10%' }} />
          <Text content="Payment Mode" style={{ ...styles.columnTextWide, width: '25%' }} />
          <Text content="Reciept No." style={{ ...styles.columnText, width: '15%' }} />
          <Text content="Payment Type" style={{ ...styles.columnText, width: '15%' }} />
          <Text content="Amount" style={{ ...styles.columnText, width: '30%' }} />
        </View>

        {data.paymentRefundVouchers && data.paymentRefundVouchers.length > 0 ? (
          data.paymentRefundVouchers.map((voucher, index) => (
            <View
              style={{
                ...styles.row,
                backgroundColor: index % 2 !== 0 ? '#ffffff' : '#f7f7f7',
                marginTop: 5,
                width: '100%'
              }}
              key={voucher._id}
            >
              <Text content={index + 1} style={{ ...styles.columnTextNarrow, width: '10%' }} />
              <Text content={data.ipd_no || '-'} style={{ ...styles.columnText, width: '10%' }} />
              <Text content={voucher.payment_mode || '-'} style={{ ...styles.columnTextWide, width: '25%' }} />
              <Text content={voucher.doc_no || '-'} style={{ ...styles.columnText, width: '15%' }} />
              <Text content={voucher.payment_type || '-'} style={{ ...styles.columnText, width: '15%' }} />
              <Text content={voucher.amount || '0'} style={{ ...styles.columnText, width: '30%' }} />
            </View>
          ))
        ) : (
          <View style={{ ...styles.row }}>
            <Text content="No Refund  Found" style={{ ...styles.columnTextNarrow, width: '100%' }} />
            <Text content="-" style={styles.columnText} />
            <Text content="-" style={styles.columnTextWide} />
            <Text content="-" style={styles.columnText} />
            <Text content="-" style={{ ...styles.columnText, width: '20%' }} />
          </View>
        )}

        <View style={{ ...styles.row, marginTop: '15%' }}>
          <Text
            content="Authorized Signatory"
            style={{ ...styles.columnTextWide, fontWeight: 'bold', marginLeft: '65%' }}
          />
          <Text content="" style={{ ...styles.columnText }} />
        </View>
        <View style={{ ...styles.row, marginTop: '0' }}>
          <Text content="Patient/Attendent Signatory:" style={{ fontWeight: 'bold', marginRight: '60%' }} />
        </View>
        <View style={{ ...styles.row, marginTop: '2%' }}>
          <Text content="Relation With Patient:" style={{ fontWeight: 'bold', marginRight: '60%' }} />
        </View>
        <View style={{ ...styles.row, marginTop: '2%' }}>
          <Text content="Mobile No.:" style={{ fontWeight: 'bold', marginRight: '60%' }} />
        </View>
      </Page>
    </Document>

  )


  if (isPublic) {
    return (
      <div style={{
        backgroundColor: "white",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Arial, sans-serif",
        padding: "20px",
        textAlign: "center"
      }}>
        <div style={{ marginBottom: "20px" }}>
          <img
            src="/logo_Hospital_m.jpeg"
            alt="Hospital Logo"
            style={{ width: "200px", height: "80px", objectFit: "contain" }}
          />
        </div>

        {isDownloaded ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#f0f9f0",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #4CAF50"
          }}>
            <div style={{ fontSize: "24px", color: "#4CAF50", marginBottom: "15px" }}>
              ✓
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#4CAF50", marginBottom: "10px" }}>
              PDF Downloaded Successfully!
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill has been downloaded. You can now close this window.
            </p>
            <button
              onClick={() => window.location.href = "about:blank"}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Close This Page
            </button>
          </div>
        ) : isError ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#fff5f5",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #f44336"
          }}>
            <div style={{ fontSize: "24px", color: "#f44336", marginBottom: "15px" }}>
              ✗
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#f44336", marginBottom: "10px" }}>
              Download Failed
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Failed to download PDF. Please try again.
            </p>
            <button
              onClick={downloadPDF}
              style={{
                backgroundColor: "#1976D2",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div style={{
            padding: "20px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            maxWidth: "400px"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px" }}>
              {isDownloading ? "Downloading PDF..." : "Preparing PDF for download..."}
            </div>

            {isDownloading && (
              <div style={{ margin: "20px 0" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #1976D2",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto"
                }} />
                <style>
                  {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
                </style>
              </div>
            )}

            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill will download automatically.
            </p>

            {!isDownloading && (
              <button
                onClick={downloadPDF}
                style={{
                  backgroundColor: "#1976D2",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginTop: "10px"
                }}
              >
                Click to Download
              </button>
            )}
          </div>
        )}
      </div>
    );
  }


  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        {PdfDoc}
      </PDFViewer>
    </div>
  );
};
